import PptxGenJS from 'pptxgenjs';
import { inchToPx, pxToIn, richTextToPptxText } from '@app/services/export/ppt/utils/formattingUtils';
import { copy, fontFace } from '@app/services/export/ppt/variables/typography';
import TextPropsOptions = PptxGenJS.TextPropsOptions;

interface IProps {
	text: string;
	textOptions?: TextPropsOptions;
	maxWidth?: number;
	useRichText?: boolean;
	hBuffer?: number;
}

interface IHelperDimensions {
	w: number; // inch
	h: number; // inch
}

export class TextWithDimensions {
	protected readonly props: IProps;

	protected textOptions: TextPropsOptions = { ...copy };

	protected dimensions: IHelperDimensions;

	protected hBuffer: number;

	public constructor(props: IProps) {
		this.props = props;
		this.textOptions = { ...this.textOptions, ...this.props.textOptions };
		this.hBuffer = this.props.hBuffer || 0.03;
		this.dimensions = this.getDimensionsFromDom();
	}

	public createForSlide(slide: PptxGenJS.Slide, options: { x: number; y: number }): void {
		const text = this.props.useRichText ? richTextToPptxText(this.props.text) : this.props.text;
		slide.addText(text, {
			...this.textOptions,
			x: options.x,
			y: options.y,
			w: this.dimensions.w,
			h: this.dimensions.h,
			align: 'left',
			valign: 'top',
			margin: 0
		});
	}

	protected getDimensionsFromDom(): IHelperDimensions {
		const domHelper = this.createDomMeasuringHelper();

		const w = pxToIn(domHelper.getBoundingClientRect().width) + this.hBuffer;
		const h = pxToIn(domHelper.getBoundingClientRect().height);

		domHelper.remove();

		return { w, h };
	}

	public getDimensions(): IHelperDimensions {
		return this.dimensions;
	}

	private createDomMeasuringHelper = (): HTMLDivElement => {
		const div = document.createElement('div');
		const ptToPx = (pt: number): number => pt * 1.33;

		div.innerHTML = this.props.text;
		div.style.fontFamily = this.textOptions.fontFace || fontFace;
		div.style.fontSize = `${Math.round(ptToPx(this.textOptions.fontSize as number))}px`;
		div.style.lineHeight = `${Math.round(ptToPx(this.textOptions.lineSpacing as number))}px`;
		div.style.letterSpacing = `${Math.round(ptToPx(this.textOptions.charSpacing as number))}px`;
		div.style.fontWeight = this.textOptions.bold ? 'bold' : 'normal';

		if (this.props.maxWidth) {
			div.style.maxWidth = `${inchToPx(this.props.maxWidth)}px`;
		}

		div.classList.add('ppt-export-text-dimension-helper');
		document.body.appendChild(div);
		return div;
	};
}
